/**
 * LUẬT THẢ TƯỚNG Ở VỊ TRÍ TỰ CHỌN — HÀM THUẦN, **0 IMPORT** (kể cả 0 import `sim/arena/types.ts`).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * FILE NÀY THAY `land-pick.ts` CŨ — ĐỌC KHỐI NÀY TRƯỚC KHI "SỬA LẠI CHO GIỐNG BẢN CŨ"
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Bản cũ là luật CHỌN LAND: sân chia 3 dải ngang, người chơi bấm 1 dải, tướng ra ở dải đó. Cả khái
 * niệm đó đã BỊ XOÁ khỏi tầng sim (xem đầu `types.ts`): sân nay là không gian 2 CHIỀU tự do có SÔNG
 * + 2 CẦU, nên "chọn land" không còn nghĩa gì.
 *
 * Người dùng chốt luật mới bằng hai câu:
 *   · *"nhà player sẽ nằm ở giữa ngoài cùng bên trái và cần to hơn, nhà địch cũng vậy nhưng bên phải"*
 *   · *"thả sai thì huỷ, báo lỗi"*
 *
 * ⇒ Luật nay là: **THẢ Ở TOẠ ĐỘ TỰ CHỌN, nhưng chỉ trong NỬA SÂN CỦA MÌNH (bờ trái, `x < 439`)**,
 * và toạ độ sai thì HUỶ lệnh + báo lỗi, KHÔNG trừ tài nguyên.
 *
 * ⚠️ CẬP NHẬT 02/10/2026 (vòng 4): thêm điều kiện **KHÔNG được thả lên MẶT CẦU** (người dùng:
 * *"Không được thả tướng lên cầu"*) — cầu là ĐƯỜNG ĐI, không phải chỗ ĐẶT quân (xem `DEPLOY_BRIDGE_*`).
 *
 * ⚠️⚠️ VÌ SAO **0 IMPORT** (KHÔNG PHẢI TÌNH CỜ — ĐỪNG "CHO GỌN" BẰNG CÁCH THÊM IMPORT).
 * File này là **vùng Phaser-free** của tầng luật thả quân: 0 `import` ⇒ chạy được trong node của
 * vitest, và `tests/arena-deploy.test.ts` khoá được toàn bộ luật bằng SỐ.
 * Bẫy đã sập ở repo (ghi nguyên văn ở bản cũ, vẫn đúng): `src/scenes/arena-battle-scene.ts` buộc
 * phải `import Phaser` (vì `extends Phaser.Scene`), mà Phaser cần DOM/ WebGL ⇒ **mọi module import
 * nó KHÔNG chạy được trong môi trường node của vitest**, đỏ bằng `ReferenceError: window is not
 * defined` ném từ `node_modules/phaser/dist/phaser.esm.js`. Đặt luật trong file scene = **luật KHÔNG
 * test được**. Không phải đánh đổi chấp nhận được.
 *
 * ⚠️ HỆ QUẢ CỐ Ý: `ARENA_RIVER` được khai LẠI quan hệ ở đây (chỉ 2 mép `x0`/`x1`) thay vì import từ
 * `river.ts`. Nhìn qua thì đó là "nguồn sự thật thứ hai", nhưng:
 *   · `river.ts` cũng 0-import, nên import nó SẼ KHÔNG phá tính chất 0-import. Nhưng nó mở ĐƯỜNG:
 *     hôm nay import `river.ts`, mai import `types.ts`, rồi tới `world.ts` (kéo `data/classes.ts` →
 *     `data/enemies.ts` → …). Vùng test thuần chỉ đứng vững nếu nó KHÔNG có import nào.
 *   · `tests/arena-deploy.test.ts` đối chiếu TỪNG SỐ với `ARENA_RIVER` thật của sim ⇒ lệch là đỏ
 *     ngay, nên "bản sao" này bị khoá chặt.
 * Đây là "bản sao có khoá" — đúng khuôn mà bản cũ đã dùng và đã ghi lý do.
 */

/**
 * MÉP TRÁI CỦA SÔNG — `x` nhỏ hơn số này là NỬA SÂN CỦA NGƯỜI CHƠI (bờ trái).
 * Bản sao có khoá của `ARENA_RIVER.x0` (xem khối chú thích đầu file).
 *
 * ⚠️ SỐ NÀY ĐÃ ĐỔI CÙNG `ARENA_RIVER`: 447 → **439** (theo ảnh mới do Lead generate). Hai chỗ phải
 * sửa CÙNG LÚC — `tests/arena-deploy.test.ts` đối chiếu từng số nên lệch là đỏ ngay.
 */
export const OWN_SHORE_X = 439;

/**
 * MÉP PHẢI CỦA SÔNG — `x` từ số này trở lên là NỬA SÂN ĐỊCH (bờ phải).
 * Bản sao có khoá của `ARENA_RIVER.x1`. Đã đổi 553 → **560** cùng lúc với `OWN_SHORE_X`.
 */
export const ENEMY_SHORE_X = 560;

/** Kích thước thế giới — bản sao có khoá của `ARENA_WORLD` (1000×600). */
export const DEPLOY_WORLD = { w: 1000, h: 600 } as const;

/**
 * LỀ AN TOÀN SÁT MÉP THẾ GIỚI — một unit có BÁN KÍNH, nên thả sát `x = 0` là nó thò ra ngoài sân.
 *
 * 🔢 **8,33** = `arenaDist(20)`, ĐÚNG bán kính của TƯỚNG (`makeHeroUnit`). Đây là bán kính LỚN NHẤT
 * trong các loại mà NGƯỜI CHƠI thả được (nông dân nhỏ hơn: `arenaDist(10)` ≈ 4,17) ⇒ lấy nó làm lề
 * thì mọi loại đều không thò ra mép.
 *
 * ⚠️⚠️ SỐ NÀY ĐÃ SAI MỘT LẦN VÀ BỊ TEST BẮT — GHI LẠI. Bản đầu tôi viết `8` (một số tròn cho đẹp),
 * nhỏ hơn bán kính tướng 8,33 một chút. Chênh 0,33 đơn vị nghe vô hại, nhưng nó nghĩa là **tướng
 * thả sát mép vẫn thò ra ngoài sân**, và `separateUnits` sẽ kéo nó vào ngay tick sau ⇒ người chơi
 * thấy tướng "tự nhảy" khỏi chỗ vừa bấm, mà không có lỗi nào được ném ra.
 * `tests/arena-deploy.test.ts` có bài đối chiếu THẲNG `DEPLOY_MARGIN` với `arenaDist(20)` của sim —
 * đó là lý do bài đó tồn tại, không phải "kiểm số cho vui".
 *
 * ⚠️ `arenaDist` cần `ARENA_XSCALE` mà file này 0-import ⇒ phải chép số đã tính sẵn. Bản sao này
 * được khoá bằng test (xem trên), nên nó không trôi khỏi `world.ts`.
 */
export const DEPLOY_MARGIN = 8.33;

/**
 * BÁN KÍNH THÂN NHÀ TÍNH TỪ TÂM (bản sao có khoá của `ARENA_CORE_BODY_RADIUS`).
 *
 * ⚠️⚠️ ĐÂY LÀ SỐ CỦA VỎ VA CHẠM, KHÔNG PHẢI `ARENA_CORE.radius` — hai số KHÁC NHAU và đã gây bẫy
 * trong repo (xem chú thích `ARENA_CORE_BODY_RADIUS` trong `world.ts`). Vỏ va chạm của Nhà CỐ Ý nhỏ
 * hơn art để nông dân vào được tầm giao hàng. Vì vậy "thả tướng trong thân Nhà" phải kiểm theo VỎ
 * VA CHẠM — kiểm theo `ARENA_CORE.radius` (24) sẽ cho phép thả ở vùng mà `separateUnits` đẩy tướng
 * ra ngay lập tức, tức lệnh "thành công" mà tướng không ở chỗ người chơi bấm.
 */
export const DEPLOY_CORE_BODY_RADIUS = 27.5;

/** TÂM NHÀ CỦA NGƯỜI CHƠI — bản sao có khoá của `ARENA_CORE.leftX`/`y` (xem chú thích `DEPLOY_MARGIN`). */
export const DEPLOY_OWN_CORE = { x: 62.5, y: 300 } as const;

/** TÂM NHÀ CỦA PHE ĐỊCH — bản sao có khoá của `ARENA_CORE.rightX`/`y`. */
export const DEPLOY_ENEMY_CORE = { x: 1000 - 62.5, y: 300 } as const;

/**
 * MẶT CẦU — VÙNG CẤM THẢ QUÂN. Bản sao có khoá của `ARENA_BRIDGE_DECK_X0/X1` +
 * `ARENA_BRIDGE_DECK_TOP/BOTTOM` trong `river.ts` (xem chú thích `DEPLOY_MARGIN` về lý do chép số).
 *
 * ⚠️⚠️ YÊU CẦU NGƯỜI DÙNG: *"Không được thả tướng lên cầu"*.
 *
 * VÌ SAO PHẢI CÓ VÙNG `x` RIÊNG (không tái dùng `OWN_SHORE_X`/`ENEMY_SHORE_X`): luật cũ chỉ chặn dải
 * sông (`439 ≤ x < 560`) nên nó chặn được phần cầu NẰM TRÊN SÔNG, nhưng mặt cầu vẽ trong ảnh còn THÒ
 * HẲN SANG HAI BỜ (trụ đá + sàn gỗ, mỗi bên 78 đơn vị). Vùng thò bên trái nằm trong nửa sân người chơi
 * ⇒ luật cũ CHO THẢ, và người chơi thấy tướng đứng trên mặt gỗ — đúng thứ vừa bị cấm.
 *
 * 🔢 ĐO TỪ ẢNH, không đặt tay: `x ∈ [361, 638)` (đối xứng quanh tâm sông 499,5), và 2 dải `y` là dải
 * MẶT GỖ (`[101,2, 213)` · `[372, 486)`). Chép số + khoá bằng test (`arena-deploy.test.ts`) — cùng
 * khuôn với `OWN_SHORE_X`.
 */
export const DEPLOY_BRIDGE_X0 = 361;
export const DEPLOY_BRIDGE_X1 = 638;
export const DEPLOY_BRIDGE_BANDS: readonly { top: number; bottom: number }[] = [
  { top: 101.2, bottom: 213 },
  { top: 372, bottom: 486 },
];

/**
 * KẾT QUẢ KIỂM MỘT TOẠ ĐỘ THẢ — `ok` + LÝ DO khi hỏng.
 *
 * ⚠️ `reason` LÀ MÃ MÁY ĐỌC ĐƯỢC (`'river'`, `'enemy-shore'`, …), KHÔNG PHẢI CÂU TIẾNG VIỆT.
 * Câu báo lỗi cho người chơi do `world.ts` dựng từ mã này. Vì sao tách: file này 0-import nên không
 * được kéo `data/arena.ts` (nơi có nhãn), mà nhét chuỗi tiếng Việt vào đây thì tầng luật lại chứa
 * chữ hiển thị — và bản EN của nó sẽ không có chỗ nào để sống. Cùng khuôn với bản cũ: nó nhận `label`
 * từ ngoài vào thay vì tự đọc `ARENA_LAND_NAME`.
 */
export interface DeployCheck {
  ok: boolean;
  reason:
    | 'ok'
    | 'not-finite'
    | 'bridge'
    | 'river'
    | 'enemy-shore'
    | 'out-of-world'
    | 'in-core';
}

// ════════════════════════════════════════════════════════════════════════════
// 1. HÌNH HỌC THUẦN
// ════════════════════════════════════════════════════════════════════════════

/** `x` có nằm trong dải sông không (half-open `[x0, x1)`, xem bản cũ về quy ước này). */
function inRiverX(x: number): boolean {
  return x >= OWN_SHORE_X && x < ENEMY_SHORE_X;
}

/** `(x,y)` có nằm TRONG VÒNG THÂN NHÀ của một trong 2 phe không (kiểm theo vỏ va chạm + bán kính unit). */
function inAnyCore(x: number, y: number, unitRadius: number): boolean {
  const min = DEPLOY_CORE_BODY_RADIUS + unitRadius;
  for (const c of [DEPLOY_OWN_CORE, DEPLOY_ENEMY_CORE]) {
    if (Math.hypot(x - c.x, y - c.y) < min) return true;
  }
  return false;
}

// ════════════════════════════════════════════════════════════════════════════
// 2. LUẬT THẢ QUÂN — TRÁI TIM CỦA FILE NÀY
// ════════════════════════════════════════════════════════════════════════════

/** `(x,y)` có nằm trên MẶT CẦU (vùng CẤM thả quân) không — xem `DEPLOY_BRIDGE_*`. */
function onDeployBridge(x: number, y: number): boolean {
  if (x < DEPLOY_BRIDGE_X0 || x >= DEPLOY_BRIDGE_X1) return false;
  for (const b of DEPLOY_BRIDGE_BANDS) {
    if (y >= b.top && y < b.bottom) return true;
  }
  return false;
}

/**
 * KIỂM MỘT TOẠ ĐỘ THẢ TƯỚNG/ NÔNG DÂN CÓ HỢP LỆ KHÔNG.
 *
 * Điều kiện hợp lệ (ĐỦ CẢ 5, người dùng chốt "thả sai thì huỷ, báo lỗi"):
 *   1. toạ độ HỮU HẠN (`NaN`/ `Infinity` là rác từ pointer chia cho zoom = 0 — xem bản cũ);
 *   2. KHÔNG nằm trên MẶT CẦU: người dùng *"Không được thả tướng lên cầu"* — cầu là ĐƯỜNG ĐI, không
 *      phải chỗ ĐẶT quân. Vùng cấm là dải x của mặt cầu vẽ `[361, 638)` × 2 dải y của mặt gỗ (xem
 *      `DEPLOY_BRIDGE_*`) — bao gồm cả phần cầu THÒ SANG HAI BỜ, không chỉ phần trên sông;
 *   3. nằm TRONG nửa sân của NGƯỜI CHƠI. Tướng KHÔNG được thả ở nửa sân địch (`x >= 560`) và KHÔNG
 *      được thả xuống sông (`439 ≤ x < 560`) — đây là LUẬT, không phải gợi ý;
 *   4. KHÔNG nằm ngoài thế giới (kèm lề `DEPLOY_MARGIN` để unit không thò ra mép);
 *   5. KHÔNG nằm trong thân Nhà (cả 2 Nhà — thả vào Nhà địch thì cũng vô nghĩa vì nó ở nửa kia,
 *      nhưng kiểm cả hai để hàm không phụ thuộc thứ tự kiểm);
 *   6. `unitRadius` phải hợp lệ.
 *
 * ⚠️ THỨ TỰ KIỂM LÀ MỘT PHẦN CỦA HỢP ĐỒNG — lý do trả về ĐÚNG một `reason`.
 * UI hiện `reason` cho người chơi ("không thả được xuống sông"), nên nếu `NaN` rơi vào nhánh
 * `'river'` thì thông báo sẽ SAI và vô dụng. Vì vậy kiểm `not-finite` TRƯỚC mọi phép so.
 *
 * ⚠️ CẦU KIỂM **TRƯỚC** SÔNG/ SÂN ĐỊCH, ĐỔI NGÀY 02/10/2026 (có chủ ý — đọc kỹ): một điểm trên cầu
 * nằm TRÊN SÔNG thì cả hai nhánh đều đúng, nhưng câu người chơi cần đọc là *"không thả được lên cầu"*,
 * không phải *"không thả được xuống sông"* — họ đang nhìn thấy mặt gỗ, không thấy nước. Trước đây luật
 * gộp cầu vào sông nên câu báo sai chỗ; nay cầu có mã riêng (`'bridge'`) và đứng trước.
 *
 * ⚠️⚠️ VÌ SAO `x` PHẢI `< 439` CHỨ KHÔNG PHẢI `x < 500` (tâm sân): "nửa sân" ở đây là NỬA SÂN THEO
 * BỜ SÔNG, không phải nửa hình học của thế giới. Cho thả tới 499 nghĩa là thả được tướng XUỐNG SÔNG
 * ở dải 439–499, và tướng đó sẽ bị `separateUnits` đẩy về bờ ngay tick sau — người chơi thấy tướng
 * "tự nhảy" một quãng, mà không có thông báo lỗi nào. Kiểm theo bờ sông thì vị trí thả LUÔN là vị trí
 * tướng đứng.
 *
 * ⚠️ `unitRadius` MẶC ĐỊNH 0, KHÔNG phải bán kính tướng: hàm này dùng chung cho tướng và nông dân
 * (bán kính khác nhau), nên giá trị mặc định phải là "không quan tâm bán kính" thay vì đoán một loại.
 * Mặc định bằng bán kính tướng sẽ làm nông dân bị chặn oan ở mép thế giới — bug im lặng.
 */
export function isValidDeployPos(x: number, y: number, unitRadius = 0): DeployCheck {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return { ok: false, reason: 'not-finite' };
  const r = Number.isFinite(unitRadius) && unitRadius > 0 ? unitRadius : 0;

  /* (2) CẦU — TRƯỚC sông/ sân địch: xem chú thích "thứ tự kiểm" ở trên về lý do. */
  if (onDeployBridge(x, y)) return { ok: false, reason: 'bridge' };

  /*
   * (3) NỬA SÂN ĐỊCH + SÔNG: gộp 2 nhánh vì cả hai đều là "x vượt qua mép trái của sông", nhưng GIỮ HAI
   * `reason` KHÁC NHAU. Người chơi cần biết mình vừa bấm xuống nước hay bấm sang sân địch — hai lỗi
   * khác nhau về nguyên nhân (bấm lệch 3px so với bấm sai hẳn nửa sân).
   */
  if (inRiverX(x)) return { ok: false, reason: 'river' };
  if (x >= ENEMY_SHORE_X) return { ok: false, reason: 'enemy-shore' };

  // (4) LỀ THẾ GIỚI — unit có bán kính nên phải cách mép ít nhất `DEPLOY_MARGIN + r`
  const m = DEPLOY_MARGIN + r;
  if (x < m || x > DEPLOY_WORLD.w - m || y < m || y > DEPLOY_WORLD.h - m) {
    return { ok: false, reason: 'out-of-world' };
  }

  if (inAnyCore(x, y, r)) return { ok: false, reason: 'in-core' };

  return { ok: true, reason: 'ok' };
}

/**
 * KÉO MỘT TOẠ ĐỘ VỀ NỬA SÂN CỦA MÌNH — dùng khi UI có một toạ độ "gần đúng" và muốn một vị trí
 * hợp lệ, thay vì huỷ lệnh.
 *
 * ⚠️⚠️ NÓ **KHÔNG** THAY THẾ `isValidDeployPos` — ĐỌC KỸ TRƯỚC KHI DÙNG.
 * Người dùng chốt *"thả sai thì huỷ, báo lỗi"* cho lệnh THẢ TƯỚNG. Nếu `summonHero` gọi hàm này thì
 * một cú bấm xuống sông sẽ biến thành "tướng ra ở mép sông" — tức lệnh SAI vẫn thành công, đúng thứ
 * người dùng bảo đừng làm. Vì vậy `summonHero`/ `buyFarmer` dùng `isValidDeployPos` và HUỶ.
 * Hàm này để dành cho chỗ CẦN một vị trí (bot tự đặt quân, hiệu ứng, debug) — nơi "không có lệnh
 * nào của người chơi bị nuốt".
 *
 * Quy tắc kéo: `x` kẹp vào `[m, OWN_SHORE_X - m]` (nửa sân mình, trừ lề và bán kính), `y` kẹp vào
 * `[m, h - m]`. Kẹp `x` theo `OWN_SHORE_X` chứ KHÔNG theo `w/2`: nửa sân là nửa BỜ SÔNG (xem
 * `isValidDeployPos`).
 */
export function clampToOwnHalf(x: number, y: number, unitRadius = 0): { x: number; y: number } {
  const r = Number.isFinite(unitRadius) && unitRadius > 0 ? unitRadius : 0;
  const m = DEPLOY_MARGIN + r;
  const fx = Number.isFinite(x) ? x : m;
  const fy = Number.isFinite(y) ? y : DEPLOY_WORLD.h / 2;
  const maxX = Math.max(m, OWN_SHORE_X - m);
  const minY = m;
  const maxY = Math.max(minY, DEPLOY_WORLD.h - m);
  return {
    x: Math.min(maxX, Math.max(m, fx)),
    y: Math.min(maxY, Math.max(minY, fy)),
  };
}

/**
 * ⚠️ KẾT QUẢ KÉO CÓ THỂ RƠI VÀO THÂN NHÀ — ĐÓ LÀ ĐIỀU ĐÚNG, ĐỪNG "SỬA".
 * Nhà người chơi nằm sát mép trái ở `(62,5, 300)`, tức BÊN TRONG nửa sân mình. Kéo một toạ độ về
 * `x = m` (≈ 8) sẽ ra chỗ KHÔNG dính Nhà, nhưng kéo về `y = 300` mà `x` lại gần 62 thì DÍNH.
 * Hàm này KHÔNG đẩy ra khỏi Nhà vì:
 *   · đẩy ra khỏi một hình tròn theo hướng nào là quyết định của gameplay (ra trước Nhà? ra sau?);
 *   · chỗ gọi CẦN vị trí hợp lệ thì gọi `isValidDeployPos` để tự kiểm;
 *   · và quan trọng nhất: "kéo về nửa sân" là bài toán HÌNH HỌC, "đứng được cạnh Nhà" là bài toán
 *     VA CHẠM — trộn hai thứ vào một hàm là nguồn của lỗi khó truy (đúng loại bẫy mà bản cũ đã ghi
 *     ở phần "vì sao không gộp 2 loại không-thuộc-land").
 * `tests/arena-deploy.test.ts` khoá hành vi này bằng một bài riêng, để nó là QUYẾT ĐỊNH chứ không
 * phải thiếu sót.
 */
